import React, { useContext, useState, useEffect, useRef, useMemo } from "react";
import { LoggerContext } from "context/LoggerContext";
import { mapSectionProps } from "../helpers/mapSectionProps"; 
import SectionObserver from "components/SectionObserver";
import { fetchApi } from "../services/APIRequest";
import { useOrganization } from "../context/OrganizationContext";
import { mapModuleProps } from "../helpers/mapModuleProps";

import DefaultSection from "./sections/DefaultSection/DefaultSection";
import HeroSection from "./sections/HeroSection/HeroSection";
import FeatureSection from "./sections/FeatureSection/FeatureSection";
import DetailsSection from "./sections/DetailsSection/DetailsSection";
import CardsSection from "./sections/CardsSection/CardsSection";
import CardsListSection from "./sections/CardsListSection/CardsListSection";
import SliderSection from "./sections/SliderSection/SliderSection";
import AccordionSection from "./sections/AccordionSection/AccordionSection";
import AnimatedStatsSection from "./sections/AnimatedStatsSection/AnimatedStatsSection";
import BreadCrumbsSection from "./sections/BreadcrumbsSection/BreadcrumbsSection";
import Tabs from "./sections/TabsSection/TabsSection";
import CTASection from "./sections/CTASection/CTASection";
import TimelineSection from "./sections/TimelineSection/TimelineSection";
import Gallery from "./sections/GallerySection/GallerySection";
import FormSection from "./sections/FormSection/FormSection";
import Footer from "./sections/FooterSection/FooterSection";
import ContainerSection from "./sections/ContainerSection/ContainerSection";
import CartSection from "./sections/CartSection/CartSection";
import CheckoutSection from "./sections/CheckoutSection/CheckoutSection";
import RedirectSection from "./sections/RedirectSection/RedirectSection";

const Section = ({ ...props }) => {
	const { section, moduleDataMapped, moduleData, moduleId, isFirst, organization } = props;
	const { logDebug, logError } = useContext(LoggerContext);
	const [localModuleData, setLocalModuleData] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);
	const org = useOrganization() || organization;
	
	// Use ref to track if data has been loaded to prevent repeated API calls
	const dataLoadedRef = useRef(false);
	
	// Request-ID für Debugging
	const currentRequestIdRef = useRef(null);
	
	// Map section props only once
	const mapped = mapSectionProps(section);
	mapped.isFirst = isFirst;
	
	// Determine the module_id for mapping and configuration
	const effectiveModuleId = useMemo(() => {
		if (section && section.module_id) {
			return section.module_id;
		} else if (section && section.global_module && section.global_module.module_id) {
			return section.global_module.module_id;
		} else {
			return moduleId;
		}
	}, [section, moduleId]);
	
	// Load section module data if needed
	useEffect(() => {
		// Skip loading if already loaded (prevents loops)
		if (dataLoadedRef.current && !section.forceReload && !section.shouldLoadOwnData) {
			return;
		}
		
		const loadSectionModuleData = async () => {
			// Load data only if needed
			if ((!section.shouldLoadOwnData && !section.global_module && !section.forceDataLoad) || !section.endpoint) return;
			
			setIsLoading(true);
			
			// Mark as loaded for this render cycle
			dataLoadedRef.current = true;
			setError(null);
			
			// Generate unique request ID for tracking
			const requestId = Math.random().toString(36).substring(2, 10);
			currentRequestIdRef.current = requestId;
			
			try {
				// Build URL parameters
				const orderBy = section.ordered ? "order_by=sort_number" : "";
				const orderByDirection = section.orderDirection ? 
					`&order_by_dir=${section.orderDirection}` : 
					"&order_by_dir=desc";
				const filters = section.moduleFilter ? `&${section.moduleFilter}` : "";
				
				// Set a default limit for API requests
				const limit = section.items_per_page || 10;
				const pagingParam = `&page=1&limit=${limit}`;
				
				// Debug identifier for this request
				logDebug(`[${requestId}] Loading module data for section`, { 
					endpoint: section.endpoint, 
					params: `${orderBy}${orderByDirection}${filters}${pagingParam}`,
					moduleId: effectiveModuleId
				});
				
				// Deduplicated URL parameters with ? or & based on position
				const fullEndpoint = `${section.endpoint}/public?${orderBy}${orderByDirection.replace(/^&/, '')}${filters}${pagingParam}`;
				const response = await fetchApi(fullEndpoint, org?.otk, true);
				
				// Check if this response is for the current request
				if (currentRequestIdRef.current !== requestId) {
					logDebug(`[${requestId}] Request superseded by newer request, ignoring response`);
					return;
				}
				
				// Process data
				let processedData = [];
				
				if (response) {
					// API typically returns paginated data
					if (response.data && Array.isArray(response.data)) {
						processedData = response.data;
						logDebug(`[${requestId}] API pagination format detected`);
					} else {
						// Fallback for non-paginated responses
						processedData = Array.isArray(response) ? response : [response];
						logDebug(`[${requestId}] Simple data format without pagination`);
					}
				}
				
				// Process data with mapModuleProps
				const mappedData = mapModuleProps(
					effectiveModuleId, 
					processedData || [], 
					"", 
					"", 
					{ logDebug, logError }
				);
				
				setLocalModuleData(mappedData);
				
				logDebug(`[${requestId}] Loaded module data for section`, { 
					mappedDataLength: mappedData?.length,
					moduleId: effectiveModuleId
				});
				
			} catch (err) {
				setError(err.message);
				logError("Error loading module data for section", err);
			} finally {
				// Reset loading state
				setIsLoading(false);
			}
		};
		
		if (org?.otk) loadSectionModuleData();
	}, [section, org?.otk, effectiveModuleId, logDebug, logError]);
	
	// Reset data loaded tracker when component unmounts
	useEffect(() => {
		return () => {
			dataLoadedRef.current = false;
			currentRequestIdRef.current = null;
		};
	}, []);
	
	// For Event modules, check if moduleDataMapped contains subsections
	useEffect(() => {
		if (effectiveModuleId === 1610 && moduleDataMapped && Array.isArray(moduleDataMapped)) {
			logDebug("Processing Event module with mapped data:", moduleDataMapped);
			
			// Look for the event object that matches this section
			const matchingEvent = moduleDataMapped.find(item => 
				item.title === section.title || 
				(moduleData && moduleData.length === 1) // If only one event exists
			);
			
			if (matchingEvent && matchingEvent.subsections) {
				logDebug("Found matching event with subsections:", matchingEvent);
				mapped.subsections = matchingEvent.subsections;
			}
		}
	}, [effectiveModuleId, moduleDataMapped, moduleData, section.title, logDebug, mapped]);
	
	// Use the appropriate data source
	// console.log("moduleDataMappedmoduleDataMapped", mapped);
	const dataToUse = localModuleData || moduleDataMapped || mapped.data;
	
	// Create props for the section components
	const sectionProps = {
		...mapped,
		data: dataToUse,
		loading: isLoading,
		error: error,
		padding_y: section.padding_y || mapped.padding_y,
		padding_x: section.padding_x || mapped.padding_x,
		margin_y: section.margin_y || mapped.margin_y,
		margin_x: section.margin_x || mapped.margin_x,
		organization: org || organization
	};
	
	// Select appropriate component based on section type
	let SectionComponent;
	const sectionType = section?.global_type?.id;
	
	switch (sectionType) {

		// Selectable by creator
		case 1: SectionComponent = HeroSection; break;
		case 11: SectionComponent = FeatureSection; break;
		case 16: SectionComponent = DetailsSection; break;
		case 21: SectionComponent = SliderSection; break;
		case 31: SectionComponent = CTASection; break;
		case 41: SectionComponent = TimelineSection; break;
		case 51: SectionComponent = CardsSection; break;
		case 52: SectionComponent = CardsListSection; break;
		case 61: SectionComponent = AccordionSection; break;
		case 71: SectionComponent = Tabs; break;
		case 81: SectionComponent = Gallery; break;
		case 91: SectionComponent = Footer; break;
		case 95: SectionComponent = BreadCrumbsSection; break;
		case 100: SectionComponent = ContainerSection; break;
		case 101: SectionComponent = AnimatedStatsSection; break;
		case 111: SectionComponent = FormSection; break;
		case 121: SectionComponent = CartSection; break;
		case 131: SectionComponent = RedirectSection; break;

		// Non selectable
		case 1000: 
			SectionComponent = CheckoutSection; 
			logDebug("Rendering CheckoutSection with data:", { 
				hasData: !!sectionProps.data,
				dataType: sectionProps.data ? typeof sectionProps.data : 'undefined',
				dataKeys: sectionProps.data ? Object.keys(sectionProps.data) : [],
				cartToken: sectionProps.cartToken,
				moduleId: effectiveModuleId
			});
			break;
		default: SectionComponent = DefaultSection; break;
	}
	
	// Debug for container layout
	if (sectionProps.containerLayout) {
		console.log(`Section ${sectionProps.title || 'unnamed'}, type ${sectionType} received containerLayout:`, sectionProps.containerLayout);
	}
	
	// Render the section content
	return (
		<SectionObserver id={section.anchor}>
			<div id={section.anchor}>
				{/* Render section component based on type */}
				{(SectionComponent === HeroSection || 
				  SectionComponent === FeatureSection || 
				  SectionComponent === CardsSection ||
				  SectionComponent === CardsListSection ||
				  SectionComponent === CheckoutSection ||
				  SectionComponent === FormSection) ? (
					// These components don't expect children
					<SectionComponent {...sectionProps} />
				) : (
					// Components that may contain children
					<SectionComponent {...sectionProps}>
						{/* Recursively render all subsections if needed */}
						{section?.global_type?.id !== 100 && mapped.subsections && Array.isArray(mapped.subsections) && mapped.subsections.length > 0 && 
							mapped.subsections.map((subsection, index) => (
								<Section 
									key={`subsection-${subsection.id || index}`}
									section={{
										...subsection,
										padding_y: subsection.padding_y ?? 0  // Use existing padding or default to 0
									}}
									moduleDataMapped={subsection.data || subsection.module_details || null}
									isFirst={false}
									organization={org || organization}
								/>
								
							))
						}
					</SectionComponent>
				)}
			</div>
		</SectionObserver>
	);
};

export default Section;